import {
    Button, Text, useToast, VStack,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { EmailType } from '@acp/shared';
import { ForwardEmailFormValues } from '../../features/email';
import { sendNotification } from '../../features/active-notice/service';
import { SendEmailForm, SendEmailModalRef } from './send-email-form';

export function SendOccupantNotificationForm({
    propertyId,
    onNotificationSent,
    confirmButtonText,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onNotificationSent: (notes?: string) => void;
}) {
    const queryClient = useQueryClient();
    const formRef = useRef<SendEmailModalRef>(null);
    const toast = useToast();

    const sendEmailMutation = useMutation({
        mutationFn: async ({
            toAddresses,
            ccAddresses,
            message,
        }: {
            toAddresses: string[];
            ccAddresses: string[];
            message: string;
        }) => {
            await sendNotification(propertyId, toAddresses, ccAddresses, message);
        },
        onSuccess: () => {
            toast({
                title: 'Email sent',
                description: 'The specified recipient(s) should receive the email shortly.',
                status: 'success',
                isClosable: true,
            });

            void queryClient.invalidateQueries({
                queryKey: ['notices'],
            });

            onNotificationSent();

            formRef.current?.resetForm();
        },
        onError: (error) => {
            toast({
                title: 'Failed to send occupant notification',
                description: error.message,
                status: 'error',
                isClosable: true,
            });
        },
    });

    const onSubmit = (vals: ForwardEmailFormValues) => {
        sendEmailMutation.mutate({
            toAddresses: vals.toEmails.map((email) => email.value),
            ccAddresses: vals.ccEmails.map((email) => email.value),
            message: vals.emailMessage,
        });
    };

    return (
        <VStack align="left" spacing={6}>
            <SendEmailForm
                ref={formRef}
                extraErrorMessage="Failed to send occupant email"
                propertyId={propertyId}
                mutation={sendEmailMutation}
                onSubmit={onSubmit}
                includePropertyManagers
                includeOccupants
                previewAvailable
                metaData={{
                    emailType: EmailType.OCCUPANT_NOTICE,
                    propertyId,
                }}
                confirmButtonText={confirmButtonText}
            />
            <Text fontSize="sm" textAlign="right" alignSelf="end">
                If you have already emailed the tenant(s) using
                your own email client or otherwise,
                {' '}
                <Button
                    variant="link"
                    color="initial"
                    onClick={() => onNotificationSent('Email sent outside of Compliance Portal')}
                >
                    click here
                </Button>
                .
            </Text>
        </VStack>
    );
}
